import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { PLATFORM_ID } from '@angular/core';
import * as Sentry from '@sentry/angular';
import type { BrowserOptions } from '@sentry/angular';
import Clarity from '@microsoft/clarity';
import { environment } from '../../environments/environment';

export const CONSENT_VIEWS = {
  intro: 'intro',
  settings: 'settings',
} as const;

export type ConsentView = (typeof CONSENT_VIEWS)[keyof typeof CONSENT_VIEWS];

export type ConsentState = Readonly<{
  version: number;
  necessary: true;
  analytics: boolean;
  consentedAt: number;
}>;

const CONSENT_VERSION = 1;
const STORAGE_KEY = 'cookie-consent-v1';
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly consentState = signal<ConsentState | null>(null);
  private readonly modalOpen = signal(false);
  private readonly view = signal<ConsentView>(CONSENT_VIEWS.intro);
  private readonly analyticsSelection = signal(false);
  private readonly sentryInitialized = signal(false);
  private readonly replayInitialized = signal(false);
  private readonly clarityInitialized = signal(false);
  private readonly initialized = signal(false);

  readonly isModalOpen = this.modalOpen.asReadonly();
  readonly currentView = this.view.asReadonly();
  readonly analyticsSelected = this.analyticsSelection.asReadonly();
  readonly hasConsent = computed(() => this.consentState() !== null);
  readonly analyticsConsented = computed(() => this.consentState()?.analytics ?? false);
  readonly isBlocking = computed(() => this.modalOpen());

  /**
   * An effect that toggles the locking of the body's scroll behavior based on the
   * state of the modal. When the modal is open, it prevents scrolling on the page
   * by adding a CSS class to the body and setting its overflow style to 'hidden'.
   * When the modal is closed, it restores the body's ability to scroll by removing
   * the CSS class and resetting the overflow style.
   *
   * This effect operates only in a browser environment and manipulates the
   * `document.body` element.
   */
  private readonly bodyLockEffect = effect(() => {
    if (!this.isBrowser) {
      return;
    }

    const body = this.document.body;
    if (this.modalOpen()) {
      body.classList.add('modal-open');
      body.style.overflow = 'hidden';
    } else {
      body.classList.remove('modal-open');
      body.style.overflow = '';
    }
  });

  /**
   * Initializes the application, setting up necessary configurations and applying user consent settings if available.
   * This method ensures initialization happens only in the browser environment and when it hasn't already been initialized.
   *
   * The initialization process includes:
   * - Setting up Sentry for error tracking.
   * - Reading and applying stored user consent information if available.
   * - Configuring the application state based on the user consent.
   * - Disabling analytics and opening the intro modal if no consent is stored.
   *
   * @return {void} No return value.
   */
  initialize(): void {
    if (!this.isBrowser || this.initialized()) {
      return;
    }

    this.initialized.set(true);
    this.initializeSentry();
    const stored = this.readConsent();
    if (stored) {
      this.consentState.set(stored);
      this.analyticsSelection.set(stored.analytics);
      this.modalOpen.set(false);
      this.view.set(CONSENT_VIEWS.intro);
      this.applyConsent(stored);
      return;
    }

    this.consentState.set(null);
    this.analyticsSelection.set(false);
    this.modalOpen.set(true);
    this.view.set(CONSENT_VIEWS.intro);
    this.disableAnalytics();
  }

  /**
   * Opens the settings view in the application.
   * Initializes necessary components, updates analytics
   * selection state, and sets the modal view to open.
   *
   * @return {void} No value is returned by this method.
   */
  openSettings(): void {
    this.initialize();
    this.analyticsSelection.set(this.consentState()?.analytics ?? false);
    this.view.set(CONSENT_VIEWS.settings);
    this.modalOpen.set(true);
  }

  /**
   * Navigates the application back to the introductory screen or view.
   * Updates the current view to 'intro'.
   *
   * @return {void} Does not return a value.
   */
  backToIntro(): void {
    this.view.set(CONSENT_VIEWS.intro);
  }

  /**
   * Grants consent for all options by saving the consent selection.
   *
   * @return {void} Does not return a value.
   */
  acceptAll(): void {
    this.saveConsent(true);
  }

  /**
   * Handles the acceptance of only the necessary consents.
   * Updates the consent preferences by storing the decision.
   *
   * @return {void} Does not return any value.
   */
  acceptNecessary(): void {
    this.saveConsent(false);
  }

  /**
   * Saves the user's selected settings for analytics preferences.
   *
   * This method invokes the `saveConsent` function using the value returned
   * from the `analyticsSelection` method to persist the user's chosen settings.
   *
   * @return {void} Does not return a value.
   */
  saveSettings(): void {
    this.saveConsent(this.analyticsSelection());
  }

  /**
   * Updates the analytics selection state.
   *
   * @param {boolean} value - The new value for analytics selection.
   * @return {void} Nothing is returned.
   */
  setAnalyticsSelection(value: boolean): void {
    this.analyticsSelection.set(value);
  }

  /**
   * Persists and updates the user's consent preferences, closing the consent modal
   * and updating the application view accordingly.
   *
   * @param {boolean} analytics - A flag indicating whether the user consents to analytics tracking.
   * @return {void}
   */
  private saveConsent(analytics: boolean): void {
    const consentedAt = this.document.defaultView?.Date?.now() ?? 0;
    const nextState: ConsentState = {
      version: CONSENT_VERSION,
      necessary: true,
      analytics,
      consentedAt,
    };

    this.persistConsent(nextState);
    this.consentState.set(nextState);
    this.modalOpen.set(false);
    this.view.set(CONSENT_VIEWS.intro);
    this.applyConsent(nextState);
  }

  /**
   * Applies the given consent state to manage analytics functionality.
   *
   * @param {ConsentState} state - The consent state object containing the user's preferences.
   * @return {void} Does not return a value.
   */
  private applyConsent(state: ConsentState): void {
    if (state.analytics) {
      this.enableAnalytics();
      return;
    }

    this.disableAnalytics();
  }

  /**
   * Enables analytics functionality by activating replay and clarity features.
   * This method initializes the required components for tracking and analyzing user interactions.
   *
   * @return {void} No return value.
   */
  private enableAnalytics(): void {
    this.updateSentryDefaultPii(true);
    this.updateSentryTracingSampleRate(true);
    this.enableReplay();
    this.enableClarity();
  }

  /**
   * Disables analytics tracking by deactivating associated services.
   *
   * This method disables replay tracking and user behavior clarity tracking.
   *
   * @return {void} This method does not return any value.
   */
  private disableAnalytics(): void {
    this.updateSentryDefaultPii(false);
    this.updateSentryTracingSampleRate(false);
    this.disableReplay();
    this.disableClarity();
  }

  /**
   * Initializes the Sentry SDK for error tracking and performance monitoring.
   * This method configures Sentry with appropriate settings based on the environment
   * and ensures the initialization is performed only once.
   *
   * @return {void} No return value.
   */
  private initializeSentry(): void {
    if (this.sentryInitialized()) {
      return;
    }

    Sentry.init({
      dsn: environment.sentryDsn,
      sendDefaultPii: false,
      environment: environment.name,
      sampleRate: 1.0,
      // Keep nested event contexts (e.g. the checkout parcel structure) fully serialized.
      normalizeDepth: 8,
      tracesSampleRate: 0,
      tracePropagationTargets: [environment.apiUrl],
      replaysOnErrorSampleRate: 0,
      replaysSessionSampleRate: 0,
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
    });

    this.sentryInitialized.set(true);
  }

  /**
   * Enables the replay functionality by initializing required configurations
   * if not already set up. This includes setting up Sentry integrations for
   * replay, adjusting replay sample rates, and starting the replay buffering process.
   *
   * @return {void} This method does not return a value.
   */
  private enableReplay(): void {
    if (!this.sentryInitialized()) {
      this.initializeSentry();
    }

    this.updateReplaySampleRates(true);
    if (!this.replayInitialized()) {
      Sentry.addIntegration(
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true
        })
      );
      this.replayInitialized.set(true);
    }

    Sentry.getReplay()?.startBuffering();
  }

  /**
   * Disables replay functionality by stopping the replay instance if it exists
   * and updating the replay sample rates to a disabled state.
   *
   * @return {void} This method does not return a value.
   */
  private disableReplay(): void {
    this.updateReplaySampleRates(false);
    const replay = Sentry.getReplay();
    if (!replay) {
      return;
    }

    void replay.stop();
  }

  /**
   * Updates the sample rates for Sentry replay functionality based on the enabled state.
   *
   * @param {boolean} enabled - Determines whether replay sampling should be enabled.
   *                            If true, sets sample rates based on the environment (production or non-production).
   *                            If false, disables sampling by setting rates to zero.
   * @return {void} Does not return a value.
   */
  private updateReplaySampleRates(enabled: boolean): void {
    const client = Sentry.getClient();
    if (!client) {
      return;
    }

    const options = client.getOptions() as BrowserOptions;
    if (enabled) {
      options.replaysOnErrorSampleRate = environment.production ? 1.0 : 0.1;
      options.replaysSessionSampleRate = environment.production ? 0.1 : 0;
      return;
    }

    options.replaysOnErrorSampleRate = 0;
    options.replaysSessionSampleRate = 0;
  }

  /**
   * Toggles Sentry default PII collection based on analytics consent.
   *
   * @param {boolean} enabled - When true, enables default PII collection.
   * @return {void} Does not return a value.
   */
  private updateSentryDefaultPii(enabled: boolean): void {
    const client = Sentry.getClient();
    if (!client) {
      return;
    }

    const options = client.getOptions() as BrowserOptions;
    options.sendDefaultPii = enabled;
  }

  /**
   * Toggles Sentry performance tracing based on analytics consent.
   *
   * @param {boolean} enabled - When true, enables performance tracing.
   * @return {void} Does not return a value.
   */
  private updateSentryTracingSampleRate(enabled: boolean): void {
    const client = Sentry.getClient();
    if (!client) {
      return;
    }

    const options = client.getOptions() as BrowserOptions;
    options.tracesSampleRate = enabled ? 1.0 : 0;
  }

  /**
   * Enables Clarity analytics tracking in a production environment if all required conditions are met.
   * Initializes Clarity with the provided clarityId and sets up necessary consent.
   *
   * @return {void} No value is returned from this method.
   */
  private enableClarity(): void {
    if (!environment.production || !environment.enableClarity || !environment.clarityId) {
      return;
    }

    if (!this.clarityInitialized()) {
      Clarity.init(environment.clarityId);
      this.clarityInitialized.set(true);
    }

    Clarity.consentV2({ ad_Storage: 'granted', analytics_Storage: 'granted' });
  }

  /**
   * Disables Clarity tracking by revoking consent for ad and analytics storage.
   *
   * This method checks if Clarity is initialized before attempting to disable it.
   * If Clarity is not initialized, the method exits without performing any action.
   *
   * @return {void} Does not return a value.
   */
  private disableClarity(): void {
    if (!this.clarityInitialized()) {
      return;
    }

    Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'denied' });
  }

  /**
   * Reads and parses the consent state from storage.
   *
   * This method retrieves the stored consent information from a designated storage,
   * validates its structure, and returns a properly formatted `ConsentState` object
   * if the data is valid. If the storage is unavailable, the data does not exist,
   * cannot be parsed, or is invalid, the method returns `null`.
   *
   * @return {ConsentState | null} The parsed and validated consent state object, or `null` if unavailable or invalid.
   */
  private readConsent(): ConsentState | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }

    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<ConsentState>;
      if (!this.isValidConsent(parsed)) {
        return null;
      }

      return {
        version: CONSENT_VERSION,
        necessary: true,
        analytics: parsed.analytics ?? false,
        consentedAt: parsed.consentedAt,
      };
    } catch {
      return null;
    }
  }

  /**
   * Persists the consent state in the storage.
   *
   * @param {ConsentState} state - The current state of user consent to be persisted.
   * @return {void} This method does not return a value.
   */
  private persistConsent(state: ConsentState): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage write failures to avoid blocking consent.
    }
  }

  /**
   * Retrieves the local storage object if the environment is a browser, otherwise returns null.
   * This method ensures compatibility with non-browser environments.
   *
   * @return {Storage | null} The local storage object if available, or null if not in a browser environment.
   */
  private getStorage(): Storage | null {
    if (!this.isBrowser) {
      return null;
    }

    return this.document.defaultView?.localStorage ?? null;
  }

  /**
   * Determines if the given value is a valid ConsentState object.
   *
   * @param {Partial<ConsentState> | null} value - The value to be validated, which may be a partial ConsentState object or null.
   * @return {boolean} - Returns true if the value is a valid ConsentState object, otherwise false.
   */
  private isValidConsent(value: Partial<ConsentState> | null): value is ConsentState {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const now = this.document.defaultView?.Date?.now() ?? 0;
    if (now === 0) {
      return false;
    }

    const consentedAt = value.consentedAt as number;
    if (now - consentedAt > ONE_YEAR_MS) {
      return false;
    }

    return (
      value.version === CONSENT_VERSION &&
      value.necessary === true &&
      typeof value.analytics === 'boolean' &&
      typeof value.consentedAt === 'number'
    );
  }
}
